import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Globe, AlertTriangle } from 'lucide-react';

const CyberThreatMap = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-blue-900/20 via-transparent to-cyan-900/20">
        <div className="max-w-7xl mx-auto text-center">
          <Globe className="w-16 h-16 text-blue-400 mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('threatMap.hero.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
            {t('threatMap.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Info Cards */}
      <section className="section-padding bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="glass-card p-6 text-center">
              <Shield className="w-10 h-10 text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{t('threatMap.info.realtime')}</h3>
              <p className="text-gray-400 text-sm">
                {t('threatMap.info.realtimeDesc')}
              </p>
            </div>
            <div className="glass-card p-6 text-center">
              <Globe className="w-10 h-10 text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{t('threatMap.info.global')}</h3>
              <p className="text-gray-400 text-sm">
                {t('threatMap.info.globalDesc')}
              </p>
            </div>
            <div className="glass-card p-6 text-center">
              <AlertTriangle className="w-10 h-10 text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">{t('threatMap.info.analytics')}</h3>
              <p className="text-gray-400 text-sm">
                {t('threatMap.info.analyticsDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Threat Map */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="glass-card p-4 w-full overflow-hidden rounded-lg">
            <iframe 
              width="100%" 
              height="798" 
              src="https://cybermap.kaspersky.com/en/widget/dynamic/dark" 
              frameBorder="0"
              title="Kaspersky Cyber Threat Map"
              className="rounded-lg"
             
            />
          </div>
          
          <div className="mt-8 glass-card p-6">
            <h3 className="text-xl font-semibold text-white mb-4">{t('threatMap.about.title')}</h3>
            <p className="text-gray-300 mb-4">
              {t('threatMap.about.desc1')}
            </p>
            <p className="text-gray-400 text-sm">
              <strong className="text-white">Note:</strong> {t('threatMap.about.desc2')}
            </p>
          </div>
        </div>
      </section>

      {/* Threat Statistics Info */}
      <section className="section-padding bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">{t('threatMap.understanding.title')}</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-white mb-4">{t('threatMap.attackTypes.title')}</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span><strong className="text-white">{t('threatMap.attackTypes.web')}</strong> {t('threatMap.attackTypes.webDesc')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span><strong className="text-white">{t('threatMap.attackTypes.network')}</strong> {t('threatMap.attackTypes.networkDesc')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span><strong className="text-white">{t('threatMap.attackTypes.local')}</strong> {t('threatMap.attackTypes.localDesc')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span><strong className="text-white">{t('threatMap.attackTypes.email')}</strong> {t('threatMap.attackTypes.emailDesc')}</span>
                </li>
              </ul>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-white mb-4">{t('threatMap.howToRead.title')}</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>{t('threatMap.howToRead.line1')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>{t('threatMap.howToRead.line2')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>{t('threatMap.howToRead.line3')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-1">•</span>
                  <span>{t('threatMap.howToRead.line4')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CyberThreatMap;
